import {
  reportPreAuthError,
  type PreAuthAction
} from '~/common/helpers/diagnostics/reportPreAuthError';
import { RECAPTCHA_SITE_KEY } from '~/common/helpers/environment';

type ExecuteRecaptchaOptions = {
  action: PreAuthAction;
  realm: string;
};

const RECAPTCHA_ACTION_BY_FLOW: Record<PreAuthAction, string> = {
  login: 'auth',
  'reset-password': 'forgotPassword',
  'first-access': 'auth'
};

/**
 * Requests a reCAPTCHA v3 token and reports a failure to Dash0 before
 * rethrowing.
 *
 * Scoring happens server-side, so a rejection here is never the user failing a
 * challenge — it means the site key was refused or the script never loaded. As
 * this runs entirely in the browser, nothing reaches auth-management and no
 * server-side log exists. Callers keep their original behaviour: the error
 * still propagates.
 *
 * @param action the flow asking for the token, also used as the report attribute
 * @param realm the realm typed on the form
 */
export async function executeRecaptcha({
  action,
  realm
}: ExecuteRecaptchaOptions): Promise<string> {
  try {
    return await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
      action: RECAPTCHA_ACTION_BY_FLOW[action]
    });
  } catch (error) {
    reportPreAuthError(error, { stage: 'recaptcha', action, realm });
    throw error;
  }
}
